
import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Hero from '../components/Hero';
import ServiceCard from '../components/ServiceCard';
import SocialProof from '../components/SocialProof';
import AboutTeam from '../components/AboutTeam';
const {FiUsers, FiTarget, FiTrendingUp, FiCheckCircle, FiArrowRight, FiCalendar, FiMail} = FiIcons;

const Home = () => {
  const services = [
    {
      icon: FiUsers,
      title: 'Recruitment',
      description: 'Find exceptional talent for robotics, AI, and computer vision roles. From junior engineers to C-level executives.',
      features: ['Permanent placement', 'Contract roles', 'Executive search']
    },
    {
      icon: FiTarget,
      title: 'HR Consulting',
      description: 'Interim HR services, compliance, and organizational structure for fast-scaling tech companies.',
      features: ['Interim HR leadership', 'Compliance & structure', 'HR strategy']
    },
    {
      icon: FiTrendingUp,
      title: 'Team Development',
      description: 'Build high-performing teams with leadership coaching, workshops, and team alignment sessions.',
      features: ['Leadership coaching', 'Team workshops', 'Performance optimization']
    }
  ];

  const stats = [
    {value: '150+', label: 'Successful Placements'},
    {value: '20+', label: 'Companies Served'},
    {value: '95%', label: 'Client Satisfaction'},
    {value: '18 days', label: 'Average Time to Hire'}
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <Hero />
      
      {/* Social Proof Section */}
      <SocialProof />
      
      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-brand-purple mb-4"> How We Help You Build the Future </h2>
            <p className="text-xl text-brand-blue max-w-3xl mx-auto">
              We combine deep industry expertise with a human-centered approach to solve your most critical talent challenges.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
              >
                <ServiceCard {...service} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{opacity: 0, scale: 0.8}}
                whileInView={{opacity: 1, scale: 1}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="text-center"
              >
                <div className="text-3xl font-bold text-brand-orange mb-2">{stat.value}</div>
                <div className="text-brand-blue">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Why Choose Trbe Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -20}}
              whileInView={{opacity: 1, x: 0}}
              transition={{duration: 0.6}}
            >
              <h2 className="text-3xl font-bold text-brand-purple mb-6"> Why Deep Tech Companies Choose Trbe </h2>
              <div className="space-y-4">
                {[
                  'Deep understanding of robotics, AI, and computer vision talent markets',
                  'People-first approach that balances technical skills with team fit',
                  'Lean, transparent processes that save you time and resources',
                  'Strategic partnership beyond just recruitment',
                  'Proven track record with startups and scale-ups'
                ].map((point, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-brand-orange mt-0.5 flex-shrink-0" />
                    <span className="text-brand-blue">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 20}}
              whileInView={{opacity: 1, x: 0}}
              transition={{duration: 0.6}}
              className="relative"
            >
              <div className="aspect-square bg-gradient-to-br from-brand-lightblue/20 to-brand-rose/20 rounded-2xl p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-6xl font-bold text-brand-orange mb-4">90%</div>
                  <div className="text-xl text-brand-blue">of our clients extend their partnership beyond the initial placement</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* About Team Section */}
      <AboutTeam />
      
      {/* Robotics & Perception Showcase Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-brand-purple mb-4"> The Future We're Building Together </h2>
            <p className="text-xl text-brand-blue max-w-3xl mx-auto">
              From autonomous systems to computer vision breakthroughs, we help companies find the talent driving innovation.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              className="relative overflow-hidden rounded-2xl shadow-lg group"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758880104739-blob" 
                alt="ABB robotic arm in clean manufacturing environment with precision automation systems" 
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-purple/80 to-transparent"></div>
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="text-xl font-semibold mb-2">Industrial Robotics</h3>
                <p className="text-sm text-brand-lightblue">Manufacturing automation & precision systems</p>
              </div>
            </motion.div>
            <motion.div
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="relative overflow-hidden rounded-2xl shadow-lg group"
            >
              <img 
                src="https://images.unsplash.com/photo-1555255707-c07966088b7b?auto=format&fit=crop&w=800&q=80" 
                alt="Computer vision and AI processing" 
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/80 to-transparent"></div>
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="text-xl font-semibold mb-2">Computer Vision</h3>
                <p className="text-sm text-brand-lightblue">AI-powered visual recognition</p>
              </div>
            </motion.div>
            <motion.div
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
              className="relative overflow-hidden rounded-2xl shadow-lg group"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758790762800-Poincloud.jpg" 
                alt="3D point cloud mapping and perception technology for autonomous systems" 
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-orange/80 to-transparent"></div>
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="text-xl font-semibold mb-2">3D Perception</h3>
                <p className="text-sm text-neutral-100">Point cloud processing & spatial mapping</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-brand-purple">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
          >
            <h2 className="text-3xl font-bold text-white mb-4"> Ready to Build Your Dream Team? </h2>
            <p className="text-xl text-brand-lightblue mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help you find the right people to build the future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://tidycal.com/trbe/exploration-call" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary-500 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiCalendar} className="h-5 w-5" />
                <span>Book a Call</span>
              </a>
              <Link 
                to="/services" 
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-brand-purple transition-colors duration-200"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Contact CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-center"
          >
            <h2 className="text-3xl font-bold text-brand-purple mb-4"> Let's Talk About Your Hiring Needs </h2>
            <p className="text-xl text-brand-blue mb-8">
              Get in touch for a free consultation about your recruitment challenges.
            </p>
            <a 
              href="mailto:hello@trbe.ai" 
              className="inline-flex items-center bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary-500 transition-colors duration-200"
            >
              <SafeIcon icon={FiMail} className="h-5 w-5 mr-2" />
              <span>Contact Us via Email</span>
            </a>
            <p className="mt-4 text-gray-600">
              Click above to open your default email application and send us a message.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
